import { useState, useEffect, useCallback } from 'react';
import { rfidBridge, type BridgeStatus, type BridgeEvent } from '../services/RfidBridgeService';

export interface UseRfidBridgeReturn {
  /** Estado actual de la conexión */
  status: BridgeStatus;
  /** UID de la tarjeta detectada (si hay) */
  cardUid: string | null;
  /** Nombre de la lectora conectada (si hay) */
  readerName: string | null;
  /** Último error ocurrido */
  error: string | null;
  /** Si está conectado y listo para leer */
  isReady: boolean;
  /** Si hay una tarjeta presente */
  hasCard: boolean;
  /** Conectar al bridge */
  connect: () => void;
  /** Desconectar del bridge */
  disconnect: () => void;
}

/**
 * Hook para interactuar con el RFID Bridge
 *
 * @example
 * ```tsx
 * const { status, cardUid, connect, isReady, hasCard } = useRfidBridge();
 *
 * return (
 *   <div>
 *     <button onClick={connect}>Conectar</button>
 *     {hasCard && <p>Tarjeta: {cardUid}</p>}
 *   </div>
 * );
 * ```
 */
export function useRfidBridge(): UseRfidBridgeReturn {
  const [status, setStatus] = useState<BridgeStatus>(rfidBridge.status);
  const [cardUid, setCardUid] = useState<string | null>(rfidBridge.currentCardUid);
  const [readerName, setReaderName] = useState<string | null>(rfidBridge.currentReaderName);
  const [error, setError] = useState<string | null>(rfidBridge.lastError);

  useEffect(() => {
    const handleEvent = (event: BridgeEvent) => {
      switch (event.type) {
        case 'status_change':
          setStatus(event.status);
          if (event.error) {
            setError(event.error);
          }
          break;

        case 'card_detected':
          setCardUid(event.uid);
          break;

        case 'card_removed':
          setCardUid(null);
          break;

        case 'reader_connected':
          setReaderName(event.readerName);
          break;

        case 'reader_disconnected':
          setReaderName(null);
          setCardUid(null);
          break;
      }
    };

    // Sincronizar estado inicial
    setStatus(rfidBridge.status);
    setCardUid(rfidBridge.currentCardUid);
    setReaderName(rfidBridge.currentReaderName);
    setError(rfidBridge.lastError);

    // Suscribirse a eventos
    return rfidBridge.subscribe(handleEvent);
  }, []);

  const connect = useCallback(() => {
    setError(null);
    rfidBridge.connect();
  }, []);

  const disconnect = useCallback(() => {
    rfidBridge.disconnect();
  }, []);

  const isReady = status === 'reader_ready' || status === 'card_present';
  const hasCard = status === 'card_present' && cardUid !== null;

  return {
    status,
    cardUid,
    readerName,
    error,
    isReady,
    hasCard,
    connect,
    disconnect,
  };
}
